import { HAZARDS } from '@cloudpearl/config';
import type { HazardKind, HazardSpawn, LandmarkSpawn, MapDef } from '@cloudpearl/config';
import { Rng, STREAM, hash32 } from './rng';
import { lerp } from './math';
import type { World } from './world';
import type { Gate, GateField } from './gates';

export interface Obstacle {
  /** Stable id: unique per seed+map. */
  id: number;
  kind: HazardKind;
  variant: number;
  x: number;
  z: number;
  /** Ground height at the root. */
  y: number;
  scale: number;
  /** Local x axis in world xz (unit). Colliders live in this frame. */
  c: number;
  s: number;
  /** Part of a grove / landmark cluster rather than a lone hazard. */
  landmark: boolean;
}

export const OBSTACLE_CELL = 48;

type Place = (kind: HazardKind, x: number, z: number, scale: number, landmark: boolean, rng: Rng) => boolean;

interface CellContext {
  rng: Rng;
  out: Obstacle[];
  x0: number;
  z0: number;
  area: number;
  progress: number;
  d: number;
  /** Mesas from neighbouring cells that smaller things must keep clear of. */
  near: Obstacle[];
  place: Place;
}

/**
 * Deterministic 2D obstacle field. Each 48 m cell is generated independently from (seed, cell).
 *
 * Fairness rules (the big lesson from v1):
 *  - nothing hides in the blind zone just past a crest — every hazard must be visible from
 *    22 m or 40 m to the south at eye height, and steep north-facing drops are off-limits;
 *  - hazards favour valleys and rising faces, where you have room to steer;
 *  - things are big and readable (scale ranges in config), never tiny ankle-biters;
 *  - wind gates keep a clear corridor.
 */
const NONE: Obstacle[] = [];

/** Drop the oldest-inserted third of a cache that has grown past `cap` (Maps keep insertion order). */
function evictOldest<K, V>(cache: Map<K, V>, cap: number): void {
  if (cache.size <= cap) return;
  let n = Math.ceil(cache.size / 3);
  for (const k of cache.keys()) {
    cache.delete(k);
    if (--n <= 0) break;
  }
}

export class ObstacleField {
  private readonly cache = new Map<number, Obstacle[]>();
  private readonly giantCache = new Map<number, Obstacle[]>();
  private readonly gateScratch: Gate[] = [];
  /** Landmark kinds big enough to span several cells (mesas), and how far they reach. */
  private readonly giantKinds = new Set<HazardKind>();
  private readonly giantReach: number = 0;

  constructor(
    private readonly world: World,
    private readonly gates: GateField,
  ) {
    for (const lm of world.map.landmarks) {
      const r = HAZARDS[lm.kind].footprint * lm.scale[1];
      if (r > OBSTACLE_CELL * 0.5) {
        this.giantKinds.add(lm.kind);
        this.giantReach = Math.max(this.giantReach, r);
      }
    }
  }

  cellOf(v: number): number {
    return Math.floor(v / OBSTACLE_CELL);
  }

  cell(ci: number, cj: number): Obstacle[] {
    const key = hash32(ci, cj, 4242);
    let list = this.cache.get(key);
    if (!list) {
      list = this.generate(ci, cj, key);
      this.cache.set(key, list);
    }
    return list;
  }

  /**
   * Free memory on long runs (cells regenerate identically on demand). Evicts the oldest third
   * rather than everything: clearing it all made the next tick regenerate every cell the
   * collision search touches at once — a visible hitch every ~30 s at speed.
   */
  prune(): void {
    evictOldest(this.cache, 1500);
    evictOldest(this.giantCache, 4000);
  }

  /** Giant landmarks of one cell (mesas), computed on their own — see `placeGiants`. */
  giantsOf(ci: number, cj: number): Obstacle[] {
    if (!this.giantKinds.size) return NONE;
    const key = hash32(ci, cj, 4242);
    const done = this.cache.get(key);
    if (done) return done.filter((o) => this.giantKinds.has(o.kind));
    let list = this.giantCache.get(key);
    if (!list) {
      const ctx = this.begin(ci, cj, key);
      this.placeGiants(ctx);
      list = ctx.out;
      this.giantCache.set(key, list);
    }
    return list;
  }

  /** Mesas from nearby cells that could reach into cell (ci, cj). */
  private giantsAround(ci: number, cj: number): Obstacle[] {
    const found: Obstacle[] = [];
    if (!this.giantKinds.size) return found;
    const R = Math.ceil((this.giantReach + 20) / OBSTACLE_CELL);
    const x0 = ci * OBSTACLE_CELL;
    const z0 = cj * OBSTACLE_CELL;
    for (let dj = -R; dj <= R; dj++) {
      for (let di = -R; di <= R; di++) {
        if (!di && !dj) continue;
        for (const g of this.giantsOf(ci + di, cj + dj)) {
          const dx = Math.max(x0 - g.x, 0, g.x - (x0 + OBSTACLE_CELL));
          const dz = Math.max(z0 - g.z, 0, g.z - (z0 + OBSTACLE_CELL));
          const r = HAZARDS[g.kind].footprint * g.scale + 20;
          if (dx * dx + dz * dz < r * r) found.push(g);
        }
      }
    }
    return found;
  }

  /** A cell's generation context: its random stream, what it has placed, and the placement rule. */
  private begin(ci: number, cj: number, key: number): CellContext {
    const w = this.world;
    const map = w.map;
    const out: Obstacle[] = [];
    const x0 = ci * OBSTACLE_CELL;
    const z0 = cj * OBSTACLE_CELL;
    const cx = x0 + OBSTACLE_CELL / 2;
    const cz = z0 + OBSTACLE_CELL / 2;
    const startR = map.terrain.startRadius;
    const progress = -cz;
    const d = w.difficulty(Math.max(0, progress));
    const rng = new Rng(w.seed, STREAM.hazards, key);
    const area = (OBSTACLE_CELL * OBSTACLE_CELL) / 10000;
    let idx = 0;
    const gates = this.gates.near(cx, cz, OBSTACLE_CELL, this.gateScratch).slice();
    const ctx: CellContext = { rng, out, x0, z0, area, progress, d, near: [], place: () => false };

    ctx.place = (kind, x, z, scale, landmark, rngv) => {
      const def = HAZARDS[kind];
      const fp = def.footprint * scale;
      if (x * x + z * z < (startR + fp) * (startR + fp)) return false;
      for (const o of out) {
        const ofp = HAZARDS[o.kind].footprint * o.scale;
        const min = landmark && o.landmark ? (fp + ofp) * 0.9 + 1.2 : fp + ofp + 9;
        const dx = o.x - x;
        const dz = o.z - z;
        if (dx * dx + dz * dz < min * min) return false;
      }
      // Nothing smaller sits inside (or pokes out of) a neighbouring cell's mesa.
      for (const g of ctx.near) {
        const min = HAZARDS[g.kind].footprint * g.scale + fp + 6;
        const dx = g.x - x;
        const dz = g.z - z;
        if (dx * dx + dz * dz < min * min) return false;
      }
      for (const g of gates) {
        const dx = g.x - x;
        const dz = g.z - z;
        const keep = 18 + fp;
        if (dx * dx + dz * dz < keep * keep) return false;
      }
      const facing = kind === 'gate' || kind === 'arch' || kind === 'house' || kind === 'ruin';
      let c: number;
      let s: number;
      if (facing) {
        // Openings face roughly north so you can thread them.
        c = 1;
        s = (rngv.float() - 0.5) * 0.6;
      } else {
        c = rngv.float() * 2 - 1;
        s = rngv.float() * 2 - 1;
        if (c * c + s * s < 1e-4) c = 1;
      }
      const l = Math.sqrt(c * c + s * s);
      out.push({ id: key * 64 + idx++, kind, variant: rngv.int(0, def.variants - 1), x, z, y: w.height(x, z), scale, c: c / l, s: s / l, landmark });
      return true;
    };
    return ctx;
  }

  /**
   * Giant landmarks (mesas) go first in every cell: everything smaller then keeps clear of them,
   * rather than one cactus vetoing a mountain. Because they come first in the cell's random
   * stream, any cell's giants can be worked out on their own (`giantsOf`) to keep neighbours clear.
   */
  private placeGiants(ctx: CellContext): void {
    for (const lm of this.world.map.landmarks) {
      if (!this.giantKinds.has(lm.kind) || (lm.minDistance !== undefined && ctx.progress < lm.minDistance)) continue;
      this.cluster(lm, ctx.rng, ctx.x0, ctx.z0, ctx.area, ctx.place);
    }
  }

  private generate(ci: number, cj: number, key: number): Obstacle[] {
    const map = this.world.map;
    const ctx = this.begin(ci, cj, key);
    const { rng, out, x0, z0, area, progress, d, place } = ctx;
    this.placeGiants(ctx);
    ctx.near = this.giantsAround(ci, cj);

    // --- lone hazards (readable, never in blind spots)
    const spawns = map.hazards.filter((h) => progress >= h.minDistance);
    if (spawns.length) {
      const km = Math.max(0, progress) / 1000;
      const weightOf = (h: HazardSpawn): number => h.weight * (1 + (h.weightGrowth ?? 0) * km);
      const expected = lerp(map.hazardDensity[0], map.hazardDensity[1], d) * area;
      let n = Math.floor(expected);
      if (rng.float() < expected - n) n++;
      for (let i = 0; i < n; i++) {
        const sp = rng.weighted(spawns, weightOf);
        const scale = rng.range(sp.scale[0], sp.scale[1]);
        const def = HAZARDS[sp.kind];
        for (let attempt = 0; attempt < 8; attempt++) {
          const x = x0 + 5 + rng.float() * (OBSTACLE_CELL - 10);
          const z = z0 + 5 + rng.float() * (OBSTACLE_CELL - 10);
          if (!this.goodSpot(x, z, def.footprint * scale + 4, 0.42)) continue;
          if (!this.visible(x, z, Math.min(3, def.colliders[0]!.y1 * scale * 0.6))) continue;
          if (place(sp.kind, x, z, scale, false, rng)) break;
        }
      }
    }

    // --- landmark clusters (groves, rock fields, hamlets, mesas)
    for (const lm of map.landmarks) {
      if (this.giantKinds.has(lm.kind) || (lm.minDistance !== undefined && progress < lm.minDistance)) continue;
      this.cluster(lm, rng, x0, z0, area, place);
    }
    return out;
  }

  private cluster(lm: LandmarkSpawn, rng: Rng, x0: number, z0: number, area: number, place: Place): void {
    const per = lm.cluster ? (lm.cluster[0] + lm.cluster[1]) / 2 : 1;
    const expected = (lm.density * area) / per;
    let n = Math.floor(expected);
    if (rng.float() < expected - n) n++;
    const fp = HAZARDS[lm.kind].footprint;
    // A mesa towers over every crest and its base is sunk deep into the sand: the "never hidden
    // past a crest" and slope rules are for things you could miss, not for mountains.
    const giant = this.giantKinds.has(lm.kind);
    for (let k = 0; k < n; k++) {
      const ccx = x0 + rng.float() * OBSTACLE_CELL;
      const ccz = z0 + rng.float() * OBSTACLE_CELL;
      const members = lm.cluster ? rng.int(lm.cluster[0], lm.cluster[1]) : 1;
      if (!giant && !this.visible(ccx, ccz, 3)) continue;
      for (let m = 0; m < members; m++) {
        const scale = rng.range(lm.scale[0], lm.scale[1]);
        const spread = clusterSpread(fp, scale, members);
        const x = ccx + (rng.float() - 0.5) * spread * 2;
        const z = ccz + (rng.float() - 0.5) * spread * 2;
        if (giant ? !this.world.solidInset(x, z, (lm.inset ?? 2) + fp * scale * 0.5) : !this.goodSpot(x, z, (lm.inset ?? 2) + fp * scale * 0.5, lm.maxSlope ?? 0.5)) continue;
        place(lm.kind, x, z, scale, true, rng);
      }
    }
  }

  /** On solid ground, inset from island rims, not too steep, not just past a north-facing crest. */
  private goodSpot(x: number, z: number, inset: number, maxSlope: number): boolean {
    const w = this.world;
    if (!w.solidInset(x, z, inset)) return false;
    const h = w.height(x, z);
    const hx = (w.height(x + 1.5, z) - w.height(x - 1.5, z)) / 3;
    const hz = (w.height(x, z + 1.5) - h) / 1.5;
    if (hx * hx + hz * hz > maxSlope * maxSlope) return false;
    // Rise per metre heading north. Steep drops are where you can't see what's coming.
    const sn = (w.height(x, z - 3) - w.height(x, z + 3)) / 6;
    return sn > -0.2;
  }

  /** Can a rider approaching from the south see this (from 22 m or 40 m away)? */
  private visible(x: number, z: number, raise: number): boolean {
    const w = this.world;
    const ty = w.height(x, z) + raise;
    for (const D of [22, 40]) {
      const vz = z + D;
      const vh = w.mask(x, vz) > 0.5 ? w.height(x, vz) : ty;
      const ey = vh + 2.6;
      let clear = true;
      for (const t of [0.25, 0.5, 0.75, 0.9]) {
        const sz = vz - D * t;
        const line = ey + (ty - ey) * t;
        if (w.mask(x, sz) > 0.5 && w.height(x, sz) > line - 0.2) {
          clear = false;
          break;
        }
      }
      if (clear) return true;
    }
    return false;
  }
}

/**
 * How far a cluster member may sit from its cluster centre (which lies inside the generating
 * cell). A lone landmark sits right on it — so a mesa stays inside the cell that stores it.
 */
export function clusterSpread(footprint: number, scale: number, members: number): number {
  return members > 1 ? 2 + footprint * scale * 1.6 + members * 1.2 : 0;
}

/**
 * The furthest an obstacle's body can reach beyond the cell that stores it (size plus cluster
 * scatter). Anything searching for "what could touch me" must scan cells this far out.
 */
export function maxReach(map: MapDef): number {
  let reach = 0;
  for (const s of map.hazards) reach = Math.max(reach, HAZARDS[s.kind].footprint * s.scale[1]);
  for (const s of map.landmarks) {
    const fp = HAZARDS[s.kind].footprint;
    reach = Math.max(reach, clusterSpread(fp, s.scale[1], s.cluster ? s.cluster[1] : 1) + fp * s.scale[1]);
  }
  return reach + 4;
}

/** Stable per-obstacle random (e.g. for visual variation) without touching generation streams. */
export function obstacleNoise(seed: number, id: number, k = 0): number {
  return hash32(seed, id, k) / 4294967296;
}
